import React, { useState, useEffect, useContext } from 'react';
import { FaDollarSign, FaChartLine, FaClock, FaTrophy, FaSync } from 'react-icons/fa';
import AppContext from '../../../context/AppContext';
import axios from 'axios';

const PackageRevenue = () => {
    const { baseUrl, authToken } = useContext(AppContext);

    const [revenue, setRevenue] = useState({
        total_revenue: 0,
        total_paid_bookings: 0,
        avg_revenue_per_booking: 0,
        pending_remaining_amount: 0,
        monthly_trend: [],
        top_packages: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchRevenue = () => {
        setLoading(true);
        setError(null);

        axios.get(`${baseUrl}/admin/packages-revenue`, {
            headers: { Authorization: `Bearer ${authToken}` }
        })
        .then((response) => {
            if (response.data.success) {
                setRevenue(response.data.data);
            } else {
                setError('Failed to load revenue data');
            }
            setLoading(false);
        })
        .catch((error) => {
            console.error('Revenue fetch error:', error);
            setError('Unable to connect to server');
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchRevenue();
    }, []);

    const maxMonthlyRevenue = Math.max(...revenue.monthly_trend.map(m => m.revenue), 1);

    if (loading) {
        return (
            <div className="adm-pkg-loading-state">
                <div className="adm-pkg-spinner"></div>
                <p>Loading revenue data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="adm-pkg-error-state">
                <p>{error}</p>
                <button className="adm-pkg-btn-retry" onClick={fetchRevenue}>
                    <FaSync /> Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="adm-pkg-revenue-container">
            {/* Revenue Stats */}
            <div className="adm-pkg-revenue-stats">
                <div className="adm-pkg-revenue-card">
                    <div className="adm-pkg-stat-icon" style={{ backgroundColor: '#E8F6F3' }}>
                        <FaDollarSign style={{ color: '#4A9E8E', fontSize: '28px' }} />
                    </div>
                    <div>
                        <h3>PKR {revenue.total_revenue.toLocaleString()}</h3>
                        <p>Total Revenue</p>
                        <span className="adm-pkg-trend-up">From fully paid bookings only</span>
                    </div>
                </div>

                <div className="adm-pkg-revenue-card">
                    <div className="adm-pkg-stat-icon" style={{ backgroundColor: '#E8F4F8' }}>
                        <FaChartLine style={{ color: '#2C5F7C', fontSize: '28px' }} />
                    </div>
                    <div>
                        <h3>{revenue.total_paid_bookings}</h3>
                        <p>Paid Bookings</p>
                        <span className="adm-pkg-trend-up">Fully confirmed &amp; paid</span>
                    </div>
                </div>

                <div className="adm-pkg-revenue-card">
                    <div className="adm-pkg-stat-icon" style={{ backgroundColor: '#FFF3E6' }}>
                        <FaTrophy style={{ color: '#FD7E14', fontSize: '28px' }} />
                    </div>
                    <div>
                        <h3>PKR {Number(revenue.avg_revenue_per_booking).toLocaleString()}</h3>
                        <p>Avg Revenue / Booking</p>
                        <span className="adm-pkg-trend-up">Per paid booking</span>
                    </div>
                </div>

                <div className="adm-pkg-revenue-card">
                    <div className="adm-pkg-stat-icon" style={{ backgroundColor: '#FFF8E1' }}>
                        <FaClock style={{ color: '#FFB800', fontSize: '28px' }} />
                    </div>
                    <div>
                        <h3>PKR {revenue.pending_remaining_amount.toLocaleString()}</h3>
                        <p>Pending Remaining Amount</p>
                        <span className="adm-pkg-trend-up">Outstanding on unpaid/partial bookings</span>
                    </div>
                </div>
            </div>

            <div className="adm-pkg-revenue-chart-card">
                <div className="adm-pkg-card-header">
                    <h3>Monthly Revenue Trend</h3>
                </div>
                <div className="adm-pkg-chart-container">
                    {revenue.monthly_trend.map((data, index) => (
                        <div key={index} className="adm-pkg-chart-bar-wrapper">
                            <div className="adm-pkg-chart-bar-container">
                                <div
                                    className="adm-pkg-chart-bar"
                                    style={{ height: `${(data.revenue / maxMonthlyRevenue) * 100}%` }}
                                >
                                    <span className="adm-pkg-bar-value">
                                        {(data.revenue / 1000).toFixed(0)}K
                                    </span>
                                </div>
                            </div>
                            <span className="adm-pkg-chart-label">{data.month}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Top Revenue Packages */}
            <div className="adm-pkg-revenue-table-card">
                <div className="adm-pkg-card-header">
                    <h3>Top Revenue Generating Packages</h3>
                </div>
                <div className="adm-pkg-revenue-table-wrapper">
                    <table className="adm-pkg-revenue-table">
                        <thead>
                            <tr>
                                <th>Rank</th>
                                <th>Package</th>
                                <th>Bookings</th>
                                <th>Price</th>
                                <th>Total Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {revenue.top_packages.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="adm-pkg-table-empty">No paid bookings yet</td>
                                </tr>
                            ) : (
                                revenue.top_packages.map((pkg, index) => (
                                    <tr key={index}>
                                        <td>#{index + 1}</td>
                                        <td>{pkg.package_name}</td>
                                        <td>{pkg.bookings_count}</td>
                                        <td>PKR {Number(pkg.price).toLocaleString()}</td>
                                        <td><strong>PKR {pkg.total_revenue.toLocaleString()}</strong></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PackageRevenue;